import { MenuItem } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { FeePostReviewTables } from "features/fees/post/FeePostReviewTables";
import {
  getFeeChargeEventRetrieveQueryKey,
  getFeeOffsetTableDataForFeeChargeEventRetrieveQueryKey,
  getFeePeriodTrueUpBreakdownRetrieveQueryKey,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import {
  fundFamilyEventPendingToCompleteUpdate,
  getFundFamilyEventRetrieveQueryKey,
  useFundFamilyEventDelete,
  useFundFamilyEventRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import { PageWizardFormFooter } from "shared/components/layout/PageWizard";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const useConfirmDeleteDraftLateOffsetsAdjustmentEvent = (
  fundFamilyId: string,
  feeChargeEventId: string,
) => {
  const { t } = useTranslation(["fees"]);
  const navigate = useNavigate();
  const { mutateAsync: deleteFundFamilyEvent } = useFundFamilyEventDelete({
    mutation: {
      onSuccess: () =>
        navigate(
          rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .fees({})
            .feeChargeEventPosted({ feeChargeEventId }).$,
        ),
    },
  });
  return useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<{
      adjustmentFundFamilyEventId: string;
    }>,
    {
      title: t("fees:lateOffsets.deleteDialog.title"),
      description: t("fees:lateOffsets.deleteDialog.description"),
      successMessage: t("fees:lateOffsets.deleteDialog.success"),
      performDelete: ({ adjustmentFundFamilyEventId }) =>
        deleteFundFamilyEvent({
          fundFamilyId,
          fundFamilyEventId: adjustmentFundFamilyEventId,
        }),
    },
  );
};

export const FeePostReviewLateOffsetsPage: React.FC = () => {
  const { t } = useTranslation(["fees", "common"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { fundFamilyId, feeChargeEventId, adjustmentFundFamilyEventId } =
    useRequiredStringParams();

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    adjustmentFundFamilyEventId,
  );

  const confirmDeleteAdjustmentEvent =
    useConfirmDeleteDraftLateOffsetsAdjustmentEvent(
      fundFamilyId,
      feeChargeEventId,
    );

  // should always be a draft for now, but doing this so we could use this page to display the posted late offsets if we want to in the future
  const isDraft = fundFamilyEventData.state === "draft";

  const form = useForm({
    defaultValues: {},
  });

  return (
    <PageContent>
      <Form
        form={form}
        onSubmit={async () =>
          await fundFamilyEventPendingToCompleteUpdate(
            fundFamilyId,
            adjustmentFundFamilyEventId,
          )
        }
        onSuccess={async () => {
          await invalidateQueryKeys(
            queryClient,
            getFeeChargeEventRetrieveQueryKey(fundFamilyId, feeChargeEventId),
            getFeeOffsetTableDataForFeeChargeEventRetrieveQueryKey(
              fundFamilyId,
              feeChargeEventId,
            ),
            getFundFamilyEventRetrieveQueryKey(
              fundFamilyId,
              adjustmentFundFamilyEventId,
            ),
            getFeePeriodTrueUpBreakdownRetrieveQueryKey(
              fundFamilyId,
              feeChargeEventId,
            ),
          );
          navigate(
            rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .fees({})
              .feeChargeEventPosted({ feeChargeEventId }).$,
          );
        }}
        successMessage={t("fees:lateOffsets.success")}
      >
        <PageHeader
          size="sm"
          title={t("fees:lateOffsets.reviewPageTitle")}
          workflowName={t("fees:lateOffsets.workflowName")}
          badge={
            isDraft
              ? {
                  text: t("fees:lateOffsets.draftOffsets"),
                  type: "warning",
                }
              : undefined
          }
          actions={
            isDraft && (
              <PageMenu>
                <MenuItem
                  className="destructive"
                  onClick={() => {
                    void confirmDeleteAdjustmentEvent({
                      adjustmentFundFamilyEventId,
                    });
                  }}
                >
                  {t("common:delete")}
                </MenuItem>
              </PageMenu>
            )
          }
        />
        <PageBody>
          <FeePostReviewTables
            fundFamilyId={fundFamilyId}
            feeChargeEventId={feeChargeEventId}
            includeDraftLateOffsets={true}
          />
          <PageWizardFormFooter />
        </PageBody>
      </Form>
    </PageContent>
  );
};
